import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
const overlayVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1
  }
};
const modalVariants = {
  hidden: {
    opacity: 0,
    y: 40,
    scale: 0.95
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.3,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  },
  exit: {
    opacity: 0,
    y: 20,
    scale: 0.97,
    transition: {
      duration: 0.2
    }
  }
};
const privacyContent = `
Última actualización: Junio 2025

1. Información que recopilamos
Recopilamos únicamente la información necesaria para procesar tu compra: nombre, dirección de correo electrónico y datos de pago. No almacenamos información de tarjetas de crédito directamente; los pagos son procesados por plataformas de pago seguras de terceros.

2. Uso de la información
Tu información personal se utiliza exclusivamente para:
• Procesar y entregar tu compra digital.
• Enviarte el producto adquirido y actualizaciones relacionadas.
• Responder a consultas de soporte si nos contactas.

3. Compartir información
No vendemos, alquilamos ni compartimos tu información personal con terceros, excepto cuando sea necesario para procesar tu pago o cuando la ley lo requiera.

4. Cookies y tecnologías de seguimiento
Nuestro sitio puede utilizar cookies básicas para mejorar la experiencia de navegación y analíticas anónimas para entender cómo los visitantes usan la página. No utilizamos cookies para publicidad dirigida.

5. Seguridad
Implementamos medidas de seguridad razonables para proteger tu información personal. Sin embargo, ningún método de transmisión por Internet es 100% seguro.

6. Tus derechos
Puedes solicitar acceso, corrección o eliminación de tus datos personales en cualquier momento.

7. Cambios a esta política
Nos reservamos el derecho de actualizar esta política en cualquier momento. Los cambios serán publicados en esta página.
`.trim();
const termsContent = `
Última actualización: Junio 2025

1. Aceptación de los términos
Al comprar y acceder a "Vistas ≠ Ventas", aceptas estos términos y condiciones en su totalidad. Si no estás de acuerdo, no realices la compra.

2. Producto digital
"Vistas ≠ Ventas" es un producto digital (PDF) que contiene información educativa, plantillas y herramientas. Se entrega en formato descargable tras la confirmación del pago.

3. Uso permitido
El producto es para tu uso personal y profesional únicamente. No está permitido:
• Redistribuir, revender o compartir el contenido con terceros.
• Reproducir el material para uso comercial sin autorización.
• Presentar el contenido como propio.

4. Sin garantía de resultados
Este producto proporciona herramientas y estrategias educativas. Los resultados dependen de múltiples factores individuales. No garantizamos ingresos específicos ni resultados financieros de ningún tipo.

5. Política de reembolso
Debido a la naturaleza digital del producto, todas las ventas son finales. No se ofrecen reembolsos una vez entregado el acceso al contenido.

6. Propiedad intelectual
Todo el contenido del producto está protegido por derechos de autor. La compra otorga una licencia de uso personal, no la transferencia de propiedad intelectual.

7. Limitación de responsabilidad
No nos hacemos responsables por decisiones tomadas basándose en el contenido del producto. El uso de las estrategias y herramientas es bajo tu propia responsabilidad.

8. Modificaciones
Nos reservamos el derecho de modificar estos términos. Las versiones actualizadas serán publicadas en esta página.
`.trim();
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const closeModal = () => setActiveModal(null);
  return <>
      <footer className="bg-[#0A0A0A] border-t border-[#1A1A1A] px-5 py-8" data-id="element-34">
        <div className="max-w-3xl mx-auto flex flex-col items-center gap-4" data-id="element-35">
          <div className="flex items-center gap-6" data-id="element-36">
            <button onClick={() => setActiveModal('privacy')} className="text-[#A0A0A0]/50 text-sm hover:text-[#E8732A] transition-colors duration-200" data-id="element-37">
              Política de Privacidad
            </button>
            <span className="text-[#A0A0A0]/20" data-id="element-38">|</span>
            <button onClick={() => setActiveModal('terms')} className="text-[#A0A0A0]/50 text-sm hover:text-[#E8732A] transition-colors duration-200" data-id="element-39">
              Términos y Condiciones
            </button>
          </div>
          <p className="text-[#A0A0A0]/30 text-xs" data-id="element-40">
            © {new Date().getFullYear()} Vistas ≠ Ventas. Todos los derechos
            reservados.
          </p>
        </div>
      </footer>

      <AnimatePresence data-id="element-41">
        {activeModal && <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-4" variants={overlayVariants} initial="hidden" animate="visible" exit="hidden" data-id="element-42">
            <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={closeModal} data-id="element-43" />

            <motion.div className="relative bg-[#141414] border border-[#2A2A2A] rounded-xl w-full max-w-2xl max-h-[80vh] flex flex-col shadow-2xl" variants={modalVariants} initial="hidden" animate="visible" exit="exit" data-id="element-44">
              <div className="flex items-center justify-between p-5 border-b border-[#2A2A2A]" data-id="element-45">
                <h2 className="text-white font-bold text-lg" data-id="element-46">
                  {activeModal === 'privacy' ? 'Política de Privacidad' : 'Términos y Condiciones'}
                </h2>
                <button onClick={closeModal} className="text-[#A0A0A0] hover:text-white transition-colors duration-200 p-1" data-id="element-47">
                  <XIcon className="w-5 h-5" data-id="element-48" />
                </button>
              </div>

              <div className="overflow-y-auto p-5 md:p-6" data-id="element-49">
                <div className="text-[#A0A0A0] text-sm leading-relaxed whitespace-pre-line" data-id="element-50">
                  {activeModal === 'privacy' ? privacyContent : termsContent}
                </div>
              </div>

              <div className="p-4 border-t border-[#2A2A2A] text-center" data-id="element-51">
                <button onClick={closeModal} className="text-[#E8732A] hover:text-[#d4662a] font-semibold text-sm transition-colors duration-200" data-id="element-52">
                  Cerrar
                </button>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}